"use client";

import { createStore, useStore, StoreApi } from "zustand";
import {
  useEffect,
  useRef,
  createContext,
  useContext,
  ReactNode,
  useMemo,
} from "react";

export interface InterviewContext {
  company?: {
    name?: string;
    id?: string;
  };
  candidate?: {
    org_candidate_id: string;
    position: string;
    name: string;
  };
  brandedAssets: {
    logoUrl: string;
    colors: {
      background: string;
      foreground: string;
    };
  } | null;
  duration?: number;
  redirectUrl?: string;
  assistant: {
    provider: "vapi";
    assistant_id?: string;
  };
  apiVersion: "0" | "1";
  interviewToken?: string;
  welcomeVideoUrl?: string;
}

export interface ElapsedTimeCallback {
  id: string;
  delay: number;
  callback: () => void;
  shouldCall: boolean;
}

interface StoreState {
  context: InterviewContext | null;
  elapsedTime: number;
  lastTimeRef: number;
  elapsedTimeCallbacks: ElapsedTimeCallback[];
  timerInitialized: boolean;
  animationFrameId: number | null;
  setElapsedTime: (time: number) => void;
  unregisterElapsedTimeCallback: (id: string) => void;
  updateCallbackShouldCall: (id: string, shouldCall: boolean) => void;
  setTimerInitialized: (initialized: boolean) => void;
  setAnimationFrameId: (id: number | null) => void;
  resetStore: () => void;
}

type InterviewStore = StoreApi<StoreState>;

const InterviewStoreContext = createContext<InterviewStore | null>(null);

export const InterviewProvider = ({
  children,
  initialContext,
}: {
  children: ReactNode;
  initialContext?: InterviewContext;
}) => {
  const store = useMemo(
    () =>
      createStore<StoreState>((set) => ({
        context: initialContext || null,
        elapsedTime: 0,
        lastTimeRef: 0,
        elapsedTimeCallbacks: [],
        timerInitialized: false,
        animationFrameId: null,
        setElapsedTime: (time: number) => set({ elapsedTime: time }),
        unregisterElapsedTimeCallback: (id: string) =>
          set((state) => ({
            elapsedTimeCallbacks: state.elapsedTimeCallbacks.filter(
              (cb) => cb.id !== id,
            ),
          })),
        updateCallbackShouldCall: (id: string, shouldCall: boolean) =>
          set((state) => ({
            elapsedTimeCallbacks: state.elapsedTimeCallbacks.map((cb) =>
              cb.id === id ? { ...cb, shouldCall } : cb,
            ),
          })),
        setTimerInitialized: (initialized: boolean) =>
          set({ timerInitialized: initialized }),
        setAnimationFrameId: (id: number | null) =>
          set({ animationFrameId: id }),
        resetStore: () =>
          set({
            elapsedTime: 0,
            elapsedTimeCallbacks: [],
            timerInitialized: false,
            animationFrameId: null,
          }),
      })),
    [initialContext],
  );

  return (
    <InterviewStoreContext.Provider value={store}>
      {children}
    </InterviewStoreContext.Provider>
  );
};

InterviewProvider.displayName = "InterviewProvider";

export const useInterviewStore = () => {
  const store = useContext(InterviewStoreContext);
  if (!store) {
    throw new Error("useInterviewStore must be used within InterviewProvider");
  }
  return useStore(store);
};

export const useInterviewStoreContext = () => {
  const store = useContext(InterviewStoreContext);
  if (!store) {
    throw new Error("useInterviewStore must be used within InterviewProvider");
  }
  return useStore(store, (s) => s.context);
};

const useInterviewStoreApi = () => {
  const store = useContext(InterviewStoreContext);
  if (!store) {
    throw new Error(
      "useInterviewStoreApi must be used within InterviewProvider",
    );
  }
  return store;
};

export const useInterviewTimer = (callbacks?: ElapsedTimeCallback[]) => {
  const {
    elapsedTime,
    elapsedTimeCallbacks,
    timerInitialized,
    setTimerInitialized,
    animationFrameId,
    setAnimationFrameId,
  } = useInterviewStore();
  const store = useInterviewStoreApi();
  const lastTimeRef = useRef<number>(0);

  useEffect(() => {
    if (!callbacks) return;
    callbacks.forEach((newCallback) => {
      const exists = elapsedTimeCallbacks.some(
        (cb) => cb.id === newCallback.id,
      );
      if (!exists) {
        elapsedTimeCallbacks.push(newCallback);
      }
    });
  }, [callbacks, elapsedTimeCallbacks]);

  const startTimer = () => {
    if (timerInitialized) return;
    setTimerInitialized(true);

    const tick = (currentTime: number) => {
      const state = store.getState();
      if (state.lastTimeRef === 0) {
        state.lastTimeRef = currentTime;
      }
      const deltaTime = currentTime - state.lastTimeRef;

      if (deltaTime >= 1000) {
        const newElapsedTime = elapsedTime + Math.floor(deltaTime / 1000);
        state.elapsedTime = newElapsedTime;
        const callbacks = state.elapsedTimeCallbacks;

        callbacks.forEach((cb) => {
          if (cb.shouldCall && newElapsedTime % cb.delay === 0) {
            cb.callback();
          }
        });

        state.lastTimeRef = currentTime;
      }

      state.setAnimationFrameId(requestAnimationFrame(tick));
    };

    setAnimationFrameId(requestAnimationFrame(tick));
  };

  useEffect(() => {
    startTimer();
    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        setAnimationFrameId(null);
        setTimerInitialized(false);
        lastTimeRef.current = 0;
      }
    };
  }, [animationFrameId, setAnimationFrameId, setTimerInitialized]);

  return {
    elapsedTime,
    startTimer,
  };
};

type TimeUnit = "ms" | "s" | "sec" | "m" | "min" | "mins" | "h" | "hr" | "hrs";

type TimeInput = number | `${number}${TimeUnit}`;

function parseTimeInput(input: TimeInput): number {
  if (typeof input === "number") {
    return input;
  }

  const match = input.match(/^(\d+)\s*(ms|s|sec|m|min|mins|h|hr|hrs)$/);
  if (!match) {
    throw new Error(
      `Invalid time format: "${input}". Expected format like "15 mins", "2 hrs", etc.`,
    );
  }

  const [, value, unit] = match;
  const numValue = parseInt(value, 10);

  switch (unit) {
    case "ms":
      return numValue;
    case "s":
    case "sec":
      return numValue * 1000;
    case "m":
    case "min":
    case "mins":
      return numValue * 60 * 1000;
    case "h":
    case "hr":
    case "hrs":
      return numValue * 60 * 60 * 1000;
    default:
      throw new Error(`Unknown time unit: "${unit}"`);
  }
}

export function elapsedTimeFn(
  timeInput: TimeInput,
  callback: () => void,
): ElapsedTimeCallback {
  const timeMs = parseTimeInput(timeInput);
  const callbackStr = callback.toString();
  const hash = btoa(callbackStr);
  const id = `callback_${timeMs}_${hash}`;

  return {
    id,
    callback,
    delay: timeMs % 60,
    shouldCall: true,
  };
}

export function secondsToFormattedMinutes(secs: number): string {
  const totalSeconds = secs;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  const formatter = new Intl.NumberFormat("en-US", {
    minimumIntegerDigits: 2,
    useGrouping: false,
  });

  return `${formatter.format(minutes)}:${formatter.format(seconds)}`;
}
